import React from 'react';
import Icon from 'components/AppIcon';
import { motion } from 'framer-motion';
import PricingSelection from './PricingSelection';
import { useAuth } from 'contexts/AuthContext';

const WebinarDescription = ({
  description,
  webinar,
  pricing,
  selectedOptions,
  setSelectedOptions,
  handleDirectCheckout,
  cartItems,
  navigate,
  isValidSelection,
  getAdminQuickActions
}) => {
  const { user, isAuthenticated, token } = useAuth();
  
  return (
    <div className="max-w-8xl mx-auto px-3 sm:px-4 lg:px-8 py-6 sm:py-8">
      {/* Admin Quick Actions */}
      {getAdminQuickActions() && (
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {getAdminQuickActions()}
        </div>
      )}

      <div className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-6">
        {/* Left Side: 65% - Webinar Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="w-full lg:w-[65%] bg-white backdrop-blur-sm rounded-xl p-6 sm:p-8 md:p-10 border border-white/20 shadow-xl overflow-hidden"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center border-b border-gray-200 pb-3">
            <Icon name="BookOpen" size={20} className="mr-3 text-blue-600" />
            Webinar Details
          </h3>
          
          {/* HTML Content */}
          <div 
            className="webinar-content text-gray-900 leading-relaxed break-words overflow-wrap-anywhere
                      [&>ul]:space-y-3 [&>ul]:my-6 [&>ul]:ml-0 [&>ul]:pl-0
                      [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:list-none
                      [&>ul>li]:text-gray-900 [&>ul>li]:text-sm [&>ul>li]:leading-relaxed [&>ul>li]:break-words
                      
                      [&>h1]:text-gray-900 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mt-6 [&>h1]:mb-4 [&>h1]:border-b [&>h1]:border-blue-600/50 [&>h1]:pb-2 [&>h1]:break-words
                      [&>h2]:text-gray-900 [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mt-5 [&>h2]:mb-3 [&>h2]:break-words
                      [&>h3]:text-gray-900 [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:mt-4 [&>h3]:mb-2 [&>h3]:break-words
                      [&>h4]:text-gray-800 [&>h4]:text-base [&>h4]:font-semibold [&>h4]:mt-3 [&>h4]:mb-2 [&>h4]:break-words
                      [&>p]:text-gray-900 [&>p]:mb-4 [&>p]:leading-relaxed [&>p]:text-sm [&>p]:break-words
                      [&>strong]:text-gray-900 [&>strong]:text-2xl [&>strong]:font-bold [&>strong]:break-words
                      [&>b]:text-gray-900 [&>b]:font-bold [&>b]:break-words
                      [&>em]:text-blue-700 [&>em]:italic [&>em]:break-words
                      [&>a]:text-blue-600 [&>a]:underline [&>a]:font-medium hover:[&>a]:text-blue-800 [&>a]:break-all
                      [&>blockquote]:border-l-4 [&>blockquote]:border-blue-600 [&>blockquote]:pl-6 [&>blockquote]:py-4 [&>blockquote]:my-6 [&>blockquote]:italic [&>blockquote]:text-blue-800 [&>blockquote]:bg-blue-50 [&>blockquote]:break-words
                      [&>code]:bg-gray-100 [&>code]:text-gray-900 [&>code]:px-2 [&>code]:py-1 [&>code]:rounded [&>code]:text-xs [&>code]:border [&>code]:break-all
                      [&>pre]:bg-gray-100 [&>pre]:text-gray-900 [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:overflow-x-auto [&>pre]:my-4 [&>pre]:border
                      [&>img]:max-w-full [&>img]:h-auto [&>img]:rounded-lg [&>img]:my-6 [&>img]:mx-auto [&>img]:shadow-lg
                      [&>table]:w-full [&>table]:bg-white [&>table]:rounded-lg [&>table]:overflow-hidden [&>table]:my-6 [&>table]:border [&>table]:table-fixed
                      [&>th]:text-gray-900 [&>th]:font-semibold [&>th]:p-3 [&>th]:text-left [&>th]:bg-gray-50 [&>th]:border-b [&>th]:break-words
                      [&>td]:p-3 [&>td]:text-gray-900 [&>td]:border-b [&>td]:border-gray-200 [&>td]:break-words
                      [&>hr]:border-0 [&>hr]:h-px [&>hr]:bg-gradient-to-r [&>hr]:from-transparent [&>hr]:via-blue-600 [&>hr]:to-transparent [&>hr]:my-6"
            dangerouslySetInnerHTML={{ __html: description }}
          />
          
          <style jsx>{`
            .webinar-content {
              word-wrap: break-word;
              overflow-wrap: break-word;
              word-break: break-word;
              hyphens: auto;
            }
            
            .webinar-content ul {
              margin: 24px 0;
              padding: 0;
              list-style: none;
              display: flex;
              flex-direction: column;
              gap: 12px;
            }
            
            .webinar-content ul li {
              display: flex;
              align-items: flex-start;
              list-style: none;
              color: rgb(17, 24, 39);
              font-size: 14px;
              line-height: 1.6;
              font-weight: 500;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            
            .webinar-content ul li::before {
              content: '✓';
              display: flex;
              align-items: center;
              justify-content: center;
              width: 20px;
              height: 20px;
              background: linear-gradient(to right, #1E90FF, #1c86ee);
              border-radius: 50%;
              color: white;
              font-size: 11px;
              font-weight: bold;
              margin-right: 12px;
              margin-top: 2px;
              flex-shrink: 0;
              box-shadow: 0 2px 4px rgba(30, 144, 255, 0.3);
            }
            
            @media (min-width: 640px) {
              .webinar-content ul li::before {
                width: 24px;
                height: 24px;
                margin-right: 16px;
                font-size: 12px;
              }
            }
            
            .webinar-content h1 {
              color: rgb(17, 24, 39);
              font-size: 1.5rem;
              font-weight: bold;
              margin: 1.5rem 0 1rem 0;
              padding-bottom: 0.5rem;
              border-bottom: 2px solid rgba(30, 144, 255, 0.5);
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            
            .webinar-content em {
              color: rgb(28, 134, 238);
              font-style: italic;
            }
            
            .webinar-content a {
              color: rgb(30, 144, 255);
              text-decoration: underline;
              font-weight: 500;
              word-break: break-all;
              overflow-wrap: break-word;
            }
            
            .webinar-content a:hover {
              color: rgb(28, 134, 238);
            }
            
            .webinar-content blockquote {
              border-left: 4px solid rgb(30, 144, 255);
              background-color: rgba(30, 144, 255, 0.05);
              padding: 1rem 1.5rem;
              margin: 1.5rem 0;
              font-style: italic;
              color: rgb(28, 134, 238);
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
          `}</style>
        </motion.div>

        {/* Right Side: 35% - Pricing Selection */}
        <div className="w-full lg:w-[35%]">
          <div className="lg:top-6">
            <PricingSelection
              webinar={webinar}
              pricing={webinar.pricing}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              handleDirectCheckout={handleDirectCheckout}
              cartItems={cartItems}
              navigate={navigate}
              isValidSelection={isValidSelection}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebinarDescription;
